import React, { FC } from 'react';
import { Box, Checkbox, Paper, Typography } from '@mui/material';
import { formatDate } from 'utils/format';
import { MessageEntry } from './subcontract-message';

type CommunicationPanelProps = {
    title: string;
    entries: MessageEntry[];
    emptyText: string;
    isSelected: boolean;
    onToggle: () => void;
    dataTest: string;
};

/**
 * One part of the parent's communication, read-only. DD-3689: the whole box is a toggle —
 * a click selects all of its content for the message to the subcontractor.
 */
const CommunicationPanel: FC<CommunicationPanelProps> = ({
    title,
    entries,
    emptyText,
    isSelected,
    onToggle,
    dataTest,
}) => (
    <Paper
        variant="outlined"
        role="checkbox"
        aria-checked={isSelected}
        aria-label={title}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                onToggle();
            }
        }}
        sx={{
            height: '100%',
            cursor: 'pointer',
            borderColor: isSelected ? 'primary.main' : undefined,
            boxShadow: (theme) => (isSelected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none'),
        }}
        data-test={dataTest}
    >
        <Box p={2}>
            <Box display="flex" alignItems="center" mb={1}>
                <Checkbox
                    checked={isSelected}
                    tabIndex={-1}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                    inputProps={{ 'aria-hidden': true }}
                />
                <Typography variant="h4">{title}</Typography>
            </Box>
            <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {entries.length ? (
                    entries.map((entry) => (
                        <Box key={entry.key} mb={1.5}>
                            {entry.author || entry.date ? (
                                <Typography variant="subtitle2">
                                    {[entry.date ? formatDate(new Date(entry.date), 'datetime') : '', entry.author]
                                        .filter(Boolean)
                                        .join(' – ')}
                                </Typography>
                            ) : undefined}
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {entry.text}
                            </Typography>
                        </Box>
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {emptyText}
                    </Typography>
                )}
            </Box>
        </Box>
    </Paper>
);

export default CommunicationPanel;
